import React, { useState, useRef } from "react";
import i18n from "i18next";

/* eslint-disable react/prop-types */

// One field's animation row beneath an expanded atomic parent track.
// Mirrors TrackClip: bar = entry-start..exit-end, entry segment at the
// LEFT edge, exit segment at the RIGHT edge.
//   Drag body         → shifts entry.delay + exit.delay together
//   Resize-left edge  → entry.delay only (bar's right pinned)
//   Resize-right edge → exit.delay only (bar's left pinned)
// All clamped within [parentStartFrame, parentEndFrame]. Durations are
// edited from the AnimationPanel.
// Ctrl/Cmd+click toggles the row into a multi-selection, Shift+click selects
// a range — the AnimationPanel then applies one preset to every selected row.
const MIN_FRAMES = 1;

const selectionMods = (e) => ({ toggle: !!(e.ctrlKey || e.metaKey), range: !!e.shiftKey });

function SubRow({
  child,
  parentStartFrame = 0,
  parentEndFrame,
  pps,
  fps,
  isSelected,
  onSelect,
  onTimingChange,
}) {
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(null);

  const hasEntry = !!child.entryName;
  const hasExit = !!child.exitName;
  const entryDurFrames = child.entryDurationFrames || 0;
  const exitDurFrames = child.exitDurationFrames || 0;

  const startFrame = pending?.startFrame ?? child.startFrame;
  const endFrame = pending?.endFrame ?? child.endFrame;
  const lifetimeFrames = endFrame - startFrame;

  const left = (startFrame / fps) * pps;
  const width = Math.max(6, (lifetimeFrames / fps) * pps);
  const startSec = (startFrame / fps).toFixed(2);
  const endSec = (endFrame / fps).toFixed(2);

  // Cap segment widths at 45% so the label between them stays visible.
  const entryW = entryDurFrames > 0 ? Math.min(width * 0.45, (entryDurFrames / fps) * pps) : 0;
  const exitW = exitDurFrames > 0 ? Math.min(width * 0.45, (exitDurFrames / fps) * pps) : 0;

  const titleParts = [];
  if (child.entryName) titleParts.push(`In: ${child.entryName}`);
  if (child.exitName) titleParts.push(`Out: ${child.exitName}`);
  titleParts.push(`${startSec}s → ${endSec}s`);

  const handleBarPointerDown = (e) => {
    if (e.target.closest(".it-subclip-resize")) return;
    e.stopPropagation();
    // Modifier clicks only change the selection (handled by the row's
    // onClick) — no drag, so a multi-select click never nudges timing.
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
    onSelect?.();

    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const origStart = child.startFrame;
    const origEnd = child.endFrame;
    const lifetime = origEnd - origStart;
    let didMove = false;

    pendingRef.current = { startFrame: origStart, endFrame: origEnd };

    const onMove = (me) => {
      const dFrames = Math.round(((me.clientX - startX) / pps) * fps);
      if (dFrames === 0) return;
      didMove = true;
      let newStart = origStart + dFrames;
      newStart = Math.max(parentStartFrame, newStart);
      newStart = Math.min(parentEndFrame - lifetime, newStart);
      const newEnd = newStart + lifetime;
      pendingRef.current = { startFrame: newStart, endFrame: newEnd };
      setPending({ startFrame: newStart, endFrame: newEnd });
    };

    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      const result = pendingRef.current;
      if (didMove && result) {
        const partial = {};
        if (hasEntry) {
          partial.entryDelay = Math.max(0, (result.startFrame - parentStartFrame) / fps);
        }
        if (hasExit) {
          const exitDurSec = exitDurFrames / fps;
          partial.exitDelay = Math.max(0, (result.endFrame - parentStartFrame) / fps - exitDurSec);
        }
        onTimingChange?.(partial);
      }
      setPending(null);
      pendingRef.current = null;
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  const handleResizeLeft = (e) => {
    e.stopPropagation();
    if (!hasEntry) return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const origStart = child.startFrame;
    const fixedEnd = child.endFrame;
    let didMove = false;

    pendingRef.current = { startFrame: origStart, endFrame: fixedEnd };

    const onMove = (me) => {
      const dFrames = Math.round(((me.clientX - startX) / pps) * fps);
      if (dFrames === 0) return;
      didMove = true;
      let newStart = origStart + dFrames;
      newStart = Math.max(parentStartFrame, newStart);
      newStart = Math.min(fixedEnd - MIN_FRAMES, newStart);
      pendingRef.current = { startFrame: newStart, endFrame: fixedEnd };
      setPending({ startFrame: newStart, endFrame: fixedEnd });
    };

    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      const result = pendingRef.current;
      if (didMove && result) {
        const entryDelay = Math.max(0, (result.startFrame - parentStartFrame) / fps);
        onTimingChange?.({ entryDelay });
      }
      setPending(null);
      pendingRef.current = null;
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  const handleResizeRight = (e) => {
    e.stopPropagation();
    if (!hasExit) return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const fixedStart = child.startFrame;
    const origEnd = child.endFrame;
    const exitDurSec = exitDurFrames / fps;
    let didMove = false;

    pendingRef.current = { startFrame: fixedStart, endFrame: origEnd };

    const onMove = (me) => {
      const dFrames = Math.round(((me.clientX - startX) / pps) * fps);
      if (dFrames === 0) return;
      didMove = true;
      let newEnd = origEnd + dFrames;
      newEnd = Math.max(fixedStart + MIN_FRAMES, newEnd);
      newEnd = Math.min(parentEndFrame, newEnd);
      pendingRef.current = { startFrame: fixedStart, endFrame: newEnd };
      setPending({ startFrame: fixedStart, endFrame: newEnd });
    };

    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      const result = pendingRef.current;
      if (didMove && result) {
        const exitDelay = Math.max(0, (result.endFrame - parentStartFrame) / fps - exitDurSec);
        onTimingChange?.({ exitDelay });
      }
      setPending(null);
      pendingRef.current = null;
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  return (
    <div
      className={[
        "it-track-row",
        "it-track-row--sub",
        isSelected ? "it-track-row--selected" : "",
      ].join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(selectionMods(e));
      }}
      onMouseDown={(e) => {
        // Shift+click would otherwise select the row labels' text.
        if (e.shiftKey) e.preventDefault();
      }}
    >
      <div className="it-track-label it-track-label--sub">
        <span className="it-track-label__name">{child.label}</span>
      </div>
      <div className="it-track-clips">
        <div
          className={["it-subclip", !hasEntry && !hasExit ? "it-subclip--empty" : ""].join(" ")}
          style={{ left, width }}
          title={
            !hasEntry && !hasExit
              ? `${child.label} — ${i18n.t("ClickToAddAnimation") || "animasyon eklemek için tıklayın"}`
              : titleParts.join(" · ")
          }
          onPointerDown={handleBarPointerDown}
        >
          {hasEntry && (
            <div
              className="it-subclip-resize it-subclip-resize--left"
              onPointerDown={handleResizeLeft}
            >
              <div className="it-subclip-resize__bar" />
            </div>
          )}
          {entryW > 0 && (
            <div
              className="it-subclip-anim-bar it-subclip-anim-bar--entry"
              style={{ width: entryW }}
              title={`In: ${child.entryName}`}
            />
          )}
          <span className="it-subclip-label">{child.label}</span>
          {exitW > 0 && (
            <div
              className="it-subclip-anim-bar it-subclip-anim-bar--exit"
              style={{ width: exitW }}
              title={`Out: ${child.exitName}`}
            />
          )}
          {hasExit && (
            <div
              className="it-subclip-resize it-subclip-resize--right"
              onPointerDown={handleResizeRight}
            >
              <div className="it-subclip-resize__bar" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubRow;
